import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, ShieldAlert } from "lucide-react";
import axios from "axios";
import { formatDateTime } from "@/lib/dates";
import MyPatriaWordmark from "@/components/patria/MyPatriaWordmark";

// Public page — NO login required. Signed magic-link acknowledgment.
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const GENERIC_ERROR = "This link is no longer valid. Please sign in and open the record from your notifications inbox to acknowledge.";

export default function AckLink() {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/magic-link/record/${token}`)
      .then((r) => setRecord(r.data))
      .catch((err) => {
        setError(err?.response?.data?.detail || GENERIC_ERROR);
      });
  }, [token]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/magic-link/acknowledge/${token}`, {
        comment: comment.trim() || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || GENERIC_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-200 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-6">
          <MyPatriaWordmark className="text-4xl" />
        </div>
        <div className="patria-card p-8" data-testid="ack-link-card">
          {error && (
            <div className="text-center py-8" data-testid="ack-link-error">
              <ShieldAlert className="h-8 w-8 mx-auto text-terracotta-600" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl text-forest-900 mt-3">Link Not Available</h2>
              <p className="text-sm text-charcoal-soft mt-2">{GENERIC_ERROR}</p>
              <a href="/login" className="patria-btn-primary mt-6 inline-flex" data-testid="ack-signin-fallback">Sign in to MyPatria</a>
            </div>
          )}

          {!error && !record && (
            <p className="text-center italic text-charcoal-muted">Verifying your link…</p>
          )}

          {!error && done && (
            <div className="text-center py-8" data-testid="ack-link-done">
              <CheckCircle2 className="h-10 w-10 mx-auto text-forest-600" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl text-forest-900 mt-3">Thank You</h2>
              <p className="text-sm text-charcoal-soft mt-2">
                Your acknowledgment has been recorded. The record is now locked
                and read-only. If you'd like to add more thoughts, sign in to
                MyPatria and add an addendum.
              </p>
              <a href="/login" className="patria-btn-secondary mt-6 inline-flex">Sign In</a>
            </div>
          )}

          {!error && record && !done && (
            <div data-testid="ack-link-view">
              <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
                Acknowledge Your 1:1 Record
              </p>
              {record.employee_name && (
                <p className="mt-1 text-xs text-charcoal-muted" data-testid="ack-for-employee">
                  For <strong>{record.employee_name}</strong>
                </p>
              )}
              <h2 className="font-serif text-3xl text-forest-900 mt-1">
                1:1 With {record.supervisor_name}
              </h2>
              <p className="text-sm text-charcoal-muted mt-1">
                Finalized {record.finalized_at ? formatDateTime(record.finalized_at) : ""} by {record.finalized_by_typed_name || record.supervisor_name}.
              </p>

              {record.already_acknowledged || record.consumed ? (
                <div className="mt-6 rounded-2xl bg-cream-100 p-4">
                  <p className="font-medium text-forest-900">Already Acknowledged</p>
                  <p className="text-xs text-charcoal-muted mt-1">This record is now read-only. Thank you.</p>
                </div>
              ) : (
                <>
                  <section className="mt-6 space-y-4">
                    {(record.item_notes || []).map((it, i) => (
                      <div key={i} className="p-3 rounded-xl bg-cream-100" data-testid={`ack-item-${i}`}>
                        {it.title && <p className="font-medium text-forest-900">{it.title}</p>}
                        {it.notes && <p className="text-sm text-charcoal-soft whitespace-pre-wrap mt-1">{it.notes}</p>}
                      </div>
                    ))}
                    {record.general_notes && (
                      <div className="p-3 rounded-xl bg-cream-100">
                        <p className="text-[10px] uppercase tracking-widest text-charcoal-muted font-medium">General Notes</p>
                        <p className="text-sm text-charcoal-soft whitespace-pre-wrap mt-1">{record.general_notes}</p>
                      </div>
                    )}
                  </section>

                  <div className="mt-6">
                    <label className="block text-xs uppercase tracking-widest text-charcoal-muted font-medium mb-1.5" htmlFor="ack-comment">
                      Optional Reply Or Comment
                    </label>
                    <textarea
                      id="ack-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder="Anything you'd like to add before acknowledging?"
                      className="patria-input w-full"
                      data-testid="ack-comment-input"
                    />
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={submit}
                      disabled={submitting}
                      className="patria-btn-primary"
                      data-testid="btn-ack-submit"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                      {submitting ? "Acknowledging…" : "Acknowledge Record"}
                    </button>
                  </div>
                  <p className="text-[10px] italic text-charcoal-muted mt-3">
                    This link is single-use and expires in 7 days. Your acknowledgment is audit-logged as "via email link".
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
